import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  Filter,
  Loader2,
  Mail,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  AnalyticsEmailRow,
  AttributeBreakdown,
  EngagementSummary,
} from "@/lib/types";
import { cn } from "@/lib/utils";

type AttributePerformance = Record<string, AttributeBreakdown[]>;

type EngagementResponse = {
  summary: EngagementSummary;
  attribute_performance: AttributePerformance;
  facets: {
    industries: string[];
    locations: string[];
    tones: string[];
    cta_types: string[];
  };
};

type Filters = {
  industry: string;
  location: string;
  tone: string;
  cta_type: string;
};

const INITIAL_FILTERS: Filters = {
  industry: "",
  location: "",
  tone: "",
  cta_type: "",
};

const STYLE_DIMENSIONS: { key: string; label: string }[] = [
  { key: "tone", label: "Tone" },
  { key: "formality", label: "Formality" },
  { key: "opening_style", label: "Opening style" },
  { key: "length_category", label: "Length" },
  { key: "personalization_level", label: "Personalization" },
  { key: "cta_type", label: "Call to action" },
  { key: "subject_line_style", label: "Subject line style" },
];

const BENEFIT_DIMENSIONS: { key: string; label: string }[] = [
  { key: "mentions_margins", label: "Mentions margins" },
  { key: "mentions_roi", label: "Mentions ROI" },
  { key: "mentions_time_savings", label: "Mentions time savings" },
  { key: "mentions_growth", label: "Mentions growth" },
  { key: "mentions_cost_reduction", label: "Mentions cost reduction" },
  { key: "mentions_social_proof", label: "Mentions social proof" },
  { key: "mentions_pain_points", label: "Mentions pain points" },
];

const RATE_COLORS = [
  "#10b981", // emerald-500 - best
  "#22c55e",
  "#84cc16",
  "#eab308",
  "#f97316",
  "#ef4444", // red-500 - worst
];

function colorForRate(rate: number, max: number, invert = false): string {
  if (max <= 0) return RATE_COLORS[3]!;
  // For "negative" metrics (bounce rate) higher is worse, so invert the scale.
  const ratio = invert ? rate / max : 1 - rate / max;
  const idx = Math.min(
    RATE_COLORS.length - 1,
    Math.max(0, Math.floor(ratio * RATE_COLORS.length))
  );
  return RATE_COLORS[idx]!;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function buildQuery(filters: Filters): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v) params.set(k, v);
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function AnalyticsPage() {
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [engagement, setEngagement] = useState<EngagementResponse | null>(null);
  const [emails, setEmails] = useState<AnalyticsEmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const [activeRate, setActiveRate] = useState<
    | "open_rate"
    | "click_rate"
    | "reply_rate"
    | "bounce_rate"
    | "waitlist_rate"
  >("reply_rate");
  const [selectedEmail, setSelectedEmail] = useState<AnalyticsEmailRow | null>(
    null
  );
  const [tableSort, setTableSort] = useState<"sent_at" | "outcome_score">(
    "sent_at"
  );

  const loadEngagement = useCallback(async (f: Filters) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/analytics/engagement${buildQuery(f)}`, {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to load");
      setEngagement(data as EngagementResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load engagement");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEmails = useCallback(async (f: Filters) => {
    setEmailsLoading(true);
    try {
      const res = await fetch(`/api/analytics/emails${buildQuery(f)}`, {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to load");
      setEmails((data.emails ?? []) as AnalyticsEmailRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load emails");
    } finally {
      setEmailsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEngagement(filters);
    loadEmails(filters);
  }, [filters, loadEngagement, loadEmails]);

  async function runSync() {
    setSyncing(true);
    setError(null);
    setActionResult(null);
    try {
      const res = await fetch("/api/analytics/sync", {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Sync failed");
      const r = data.replies ?? {};
      const w = data.waitlist ?? {};
      const rs = data.resync ?? {};
      const parts: string[] = [];
      parts.push(
        `Replies: ${r.detected ?? 0} detected from ${r.scanned ?? 0} scanned`
      );
      const newBounces = r.bounces_detected ?? 0;
      const reclassified = rs.reclassified_as_bounce ?? 0;
      if (newBounces || reclassified) {
        const bits: string[] = [];
        if (newBounces) bits.push(`${newBounces} new`);
        if (reclassified) bits.push(`${reclassified} reclassified`);
        parts.push(`Bounces: ${bits.join(" + ")}`);
      }
      if (Array.isArray(r.skipped_no_credentials) && r.skipped_no_credentials.length > 0) {
        parts.push(`Skipped (no stored Gmail token): ${r.skipped_no_credentials.length}`);
      }
      if (w.skipped) {
        parts.push(`Waitlist: skipped (${w.reason})`);
      } else {
        parts.push(`Waitlist matches: ${w.matched ?? 0} of ${w.checked ?? 0}`);
      }
      setActionResult(parts.join(" | "));
      await Promise.all([loadEngagement(filters), loadEmails(filters)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  async function runAnalyze() {
    setAnalyzing(true);
    setError(null);
    setActionResult(null);
    try {
      const res = await fetch("/api/analytics/analyze?limit=25", {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Analyze failed");
      setActionResult(`Analyzed ${data.analyzed} email(s).`);
      await Promise.all([loadEngagement(filters), loadEmails(filters)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Analyze failed");
    } finally {
      setAnalyzing(false);
    }
  }

  const sortedEmails = useMemo(() => {
    const list = [...emails];
    if (tableSort === "outcome_score") {
      list.sort((a, b) => {
        const av = a.outcome?.outcome_score ?? 0;
        const bv = b.outcome?.outcome_score ?? 0;
        return bv - av;
      });
    } else {
      list.sort(
        (a, b) =>
          new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
      );
    }
    return list;
  }, [emails, tableSort]);

  return (
    <div className="mx-auto max-w-7xl p-8 pb-20">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <BarChart3 className="h-6 w-6 text-primary" />
            Email Analytics
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            See which email styles drive the highest engagement, segmented by
            agency characteristics. Click into any cell to view the actual
            emails behind the numbers.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runAnalyze}
            disabled={analyzing}
            className="flex items-center gap-1.5 rounded-md border bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            title="Run AI attribute extraction on emails missing one"
          >
            {analyzing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Analyze new emails
          </button>
          <button
            type="button"
            onClick={runSync}
            disabled={syncing}
            className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Sync replies + waitlist
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {actionResult && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          {actionResult}
        </div>
      )}

      <FilterBar
        filters={filters}
        facets={engagement?.facets}
        onChange={setFilters}
      />

      {loading && !engagement ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : engagement ? (
        <>
          <KpiRow summary={engagement.summary} />
          <Funnel summary={engagement.summary} />
          <RateSelector value={activeRate} onChange={setActiveRate} />
          <AttributeGrid
            performance={engagement.attribute_performance}
            activeRate={activeRate}
            onCellClick={(dim, value) =>
              setFilters((f) => ({
                ...f,
                ...(dim === "tone" ? { tone: value } : {}),
                ...(dim === "cta_type" ? { cta_type: value } : {}),
              }))
            }
          />
        </>
      ) : null}

      <div className="mt-8 rounded-xl border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-base font-semibold">Emails ({emails.length})</h2>
            <p className="text-xs text-muted-foreground">
              Click any row to see the full email and contact context.
            </p>
          </div>
          <select
            value={tableSort}
            onChange={(e) =>
              setTableSort(e.target.value as typeof tableSort)
            }
            className="rounded-md border bg-white py-1.5 pl-2 pr-6 text-sm outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="sent_at">Most recent</option>
            <option value="outcome_score">Highest outcome score</option>
          </select>
        </div>
        {emailsLoading ? (
          <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading emails...
          </div>
        ) : sortedEmails.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            No emails match the current filters.
          </div>
        ) : (
          <EmailTable
            rows={sortedEmails}
            onSelect={setSelectedEmail}
            selectedId={selectedEmail?.message_id ?? null}
          />
        )}
      </div>

      {selectedEmail && (
        <EmailDetailDrawer
          row={selectedEmail}
          onClose={() => setSelectedEmail(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FilterBar({
  filters,
  facets,
  onChange,
}: {
  filters: Filters;
  facets?: EngagementResponse["facets"];
  onChange: (f: Filters) => void;
}) {
  const clear = filters.industry || filters.location || filters.tone || filters.cta_type;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border bg-card px-4 py-3">
      <Filter className="h-4 w-4 text-muted-foreground" />
      <span className="text-xs font-medium text-muted-foreground">Filters:</span>
      <select
        value={filters.industry}
        onChange={(e) => onChange({ ...filters, industry: e.target.value })}
        className="rounded-md border bg-white py-1.5 pl-2 pr-6 text-xs outline-none focus:ring-2 focus:ring-ring"
      >
        <option value="">All industries</option>
        {(facets?.industries ?? []).map((i) => (
          <option key={i} value={i}>
            {i}
          </option>
        ))}
      </select>
      <select
        value={filters.location}
        onChange={(e) => onChange({ ...filters, location: e.target.value })}
        className="rounded-md border bg-white py-1.5 pl-2 pr-6 text-xs outline-none focus:ring-2 focus:ring-ring"
      >
        <option value="">All locations</option>
        {(facets?.locations ?? []).map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </select>
      <select
        value={filters.tone}
        onChange={(e) => onChange({ ...filters, tone: e.target.value })}
        className="rounded-md border bg-white py-1.5 pl-2 pr-6 text-xs outline-none focus:ring-2 focus:ring-ring"
      >
        <option value="">Any tone</option>
        {(facets?.tones ?? []).map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <select
        value={filters.cta_type}
        onChange={(e) => onChange({ ...filters, cta_type: e.target.value })}
        className="rounded-md border bg-white py-1.5 pl-2 pr-6 text-xs outline-none focus:ring-2 focus:ring-ring"
      >
        <option value="">Any CTA</option>
        {(facets?.cta_types ?? []).map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      {clear && (
        <button
          type="button"
          onClick={() => onChange(INITIAL_FILTERS)}
          className="ml-auto text-xs text-muted-foreground hover:text-foreground"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

function KpiRow({ summary }: { summary: EngagementSummary }) {
  const cards: {
    label: string;
    value: string;
    sub: string;
    negative?: boolean;
  }[] = [
    {
      label: "Sent",
      value: summary.total_sent.toLocaleString(),
      sub: "tracked emails",
    },
    {
      label: "Open rate",
      value: pct(summary.open_rate),
      sub: `${summary.total_opened} opened`,
    },
    {
      label: "Click rate",
      value: pct(summary.click_rate),
      sub: `${summary.total_clicked} clicked`,
    },
    {
      label: "Reply rate",
      value: pct(summary.reply_rate),
      sub: `${summary.total_replied} replied`,
    },
    {
      label: "Bounce rate",
      value: pct(summary.bounce_rate),
      sub: `${summary.total_bounced} bounced`,
      negative: true,
    },
    {
      label: "Waitlist rate",
      value: pct(summary.waitlist_rate),
      sub: `${summary.total_waitlist} joined`,
    },
  ];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      {cards.map((c) => (
        <div
          key={c.label}
          className={cn(
            "rounded-xl border bg-card p-4",
            c.negative && "border-rose-200 bg-rose-50/40"
          )}
        >
          <div
            className={cn(
              "text-xs font-medium",
              c.negative ? "text-rose-700" : "text-muted-foreground"
            )}
          >
            {c.label}
          </div>
          <div
            className={cn(
              "mt-1 text-2xl font-semibold tracking-tight",
              c.negative && "text-rose-700"
            )}
          >
            {c.value}
          </div>
          <div
            className={cn(
              "text-xs",
              c.negative ? "text-rose-600/80" : "text-muted-foreground"
            )}
          >
            {c.sub}
          </div>
        </div>
      ))}
    </div>
  );
}

function Funnel({ summary }: { summary: EngagementSummary }) {
  const data = [
    { stage: "Sent", count: summary.total_sent },
    { stage: "Opened", count: summary.total_opened },
    { stage: "Clicked", count: summary.total_clicked },
    { stage: "Replied", count: summary.total_replied },
    { stage: "Waitlist", count: summary.total_waitlist },
    { stage: "Bounced", count: summary.total_bounced },
  ];
  // Bounced is a failure indicator, not a conversion stage; render in red.
  const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#ef4444"];
  return (
    <div className="mb-4 rounded-xl border bg-card p-5">
      <h3 className="mb-3 text-sm font-semibold">Conversion funnel</h3>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} layout="horizontal">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="stage" tick={{ fontSize: 11 }} stroke="#94a3b8" tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickLine={false} axisLine={false} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          />
          <Bar dataKey="count" radius={[6, 6, 0, 0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={colors[i]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

type ActiveRate =
  | "open_rate"
  | "click_rate"
  | "reply_rate"
  | "bounce_rate"
  | "waitlist_rate";

function RateSelector({
  value,
  onChange,
}: {
  value: ActiveRate;
  onChange: (v: ActiveRate) => void;
}) {
  const options: { id: ActiveRate; label: string }[] = [
    { id: "reply_rate", label: "Reply rate" },
    { id: "waitlist_rate", label: "Waitlist rate" },
    { id: "click_rate", label: "Click rate" },
    { id: "open_rate", label: "Open rate" },
    { id: "bounce_rate", label: "Bounce rate" },
  ];
  return (
    <div className="mb-3 flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">Optimize for:</span>
      <div className="inline-flex overflow-hidden rounded-md border bg-white">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={cn(
              "px-3 py-1.5 font-medium transition-colors",
              value === o.id
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-slate-50"
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function AttributeGrid({
  performance,
  activeRate,
  onCellClick,
}: {
  performance: AttributePerformance;
  activeRate: ActiveRate;
  onCellClick: (dim: string, value: string) => void;
}) {
  return (
    <div className="space-y-3">
      <Section title="Style attributes">
        {STYLE_DIMENSIONS.map((d) => (
          <AttributeCard
            key={d.key}
            label={d.label}
            rows={performance[d.key] ?? []}
            activeRate={activeRate}
            onCellClick={(value) => onCellClick(d.key, value)}
          />
        ))}
      </Section>
      <Section title="Business benefit mentions">
        {BENEFIT_DIMENSIONS.map((d) => (
          <AttributeCard
            key={d.key}
            label={d.label}
            rows={performance[d.key] ?? []}
            activeRate={activeRate}
            onCellClick={(value) => onCellClick(d.key, value)}
            yesNo
          />
        ))}
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card">
      <div className="border-b px-5 py-3 text-sm font-semibold">{title}</div>
      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
        {children}
      </div>
    </div>
  );
}

function AttributeCard({
  label,
  rows,
  activeRate,
  onCellClick,
  yesNo,
}: {
  label: string;
  rows: AttributeBreakdown[];
  activeRate: ActiveRate;
  onCellClick: (value: string) => void;
  yesNo?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-md border bg-slate-50/40 p-4">
        <div className="text-xs font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="mt-2 text-xs text-muted-foreground">No data yet.</div>
      </div>
    );
  }
  const max = rows.reduce((m, r) => Math.max(m, r[activeRate]), 0);
  return (
    <div className="rounded-md border bg-white p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-xs font-semibold">{label}</div>
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {rows.reduce((s, r) => s + r.count, 0)} samples
        </div>
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => {
          const rate = r[activeRate];
          const widthPct = Math.max(4, max ? (rate / max) * 100 : 0);
          return (
            <button
              key={r.value}
              type="button"
              onClick={() => onCellClick(r.value)}
              className="block w-full text-left"
            >
              <div className="flex items-baseline justify-between text-xs">
                <span className="capitalize">
                  {yesNo ? (r.value === "yes" ? "Yes" : "No") : r.value}
                </span>
                <span className="font-medium tabular-nums">
                  {pct(rate)}{" "}
                  <span className="text-[10px] text-muted-foreground">
                    n={r.count}
                  </span>
                </span>
              </div>
              <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: colorForRate(
                      rate,
                      max,
                      activeRate === "bounce_rate"
                    ),
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EmailTable({
  rows,
  onSelect,
  selectedId,
}: {
  rows: AnalyticsEmailRow[];
  onSelect: (r: AnalyticsEmailRow) => void;
  selectedId: string | null;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-6 py-3 font-medium">Recipient</th>
            <th className="px-6 py-3 font-medium">Subject</th>
            <th className="px-6 py-3 font-medium">Tone</th>
            <th className="px-6 py-3 font-medium">CTA</th>
            <th className="px-6 py-3 font-medium">Open / Click / Reply / Bounce / Waitlist</th>
            <th className="px-6 py-3 font-medium">Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isSelected = r.message_id === selectedId;
            const o = r.outcome;
            const a = r.attributes;
            return (
              <tr
                key={r.message_id}
                onClick={() => onSelect(r)}
                className={cn(
                  "cursor-pointer border-b last:border-b-0 transition-colors",
                  isSelected ? "bg-primary/5" : "hover:bg-slate-50/60"
                )}
              >
                <td className="px-6 py-3">
                  <div className="font-medium">
                    {r.contact_name || r.contact_email || "Unknown"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.contact_company ?? ""}
                    {r.contact_industry ? ` · ${r.contact_industry}` : ""}
                  </div>
                </td>
                <td className="max-w-xs px-6 py-3">
                  <div className="truncate">{r.subject}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(r.sent_at).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-6 py-3 text-xs capitalize">{a?.tone ?? "—"}</td>
                <td className="px-6 py-3 text-xs">{a?.cta_type ?? "—"}</td>
                <td className="px-6 py-3 text-xs">
                  <div className="flex gap-1.5">
                    <Pill ok={o?.was_opened}>Open</Pill>
                    <Pill ok={o?.was_clicked}>Click</Pill>
                    <Pill ok={o?.was_replied}>Reply</Pill>
                    <Pill ok={o?.was_bounced} negative>
                      Bounce
                    </Pill>
                    <Pill ok={o?.joined_waitlist}>Waitlist</Pill>
                  </div>
                </td>
                <td className="px-6 py-3 text-xs font-semibold tabular-nums">
                  {o?.outcome_score ?? 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Pill({
  ok,
  children,
  negative,
}: {
  ok: boolean | null | undefined;
  children: React.ReactNode;
  // For metrics where "true" is bad (bounce). Render in red when active.
  negative?: boolean;
}) {
  const activeClass = negative
    ? "bg-rose-100 text-rose-700"
    : "bg-emerald-100 text-emerald-700";
  return (
    <span
      className={cn(
        "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
        ok ? activeClass : "bg-slate-100 text-slate-500"
      )}
    >
      {children}
    </span>
  );
}

function EmailDetailDrawer({
  row,
  onClose,
}: {
  row: AnalyticsEmailRow;
  onClose: () => void;
}) {
  const [showHtml, setShowHtml] = useState(false);
  const a = row.attributes;
  const o = row.outcome;
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">{row.subject}</h3>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              To {row.contact_name || row.contact_email}
              {row.contact_company ? ` at ${row.contact_company}` : ""} on{" "}
              {new Date(row.sent_at).toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-slate-100"
          >
            ×
          </button>
        </div>

        <div className="space-y-4 p-6">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Detail label="Industry" value={row.contact_industry} />
            <Detail label="Location" value={row.contact_location} />
            <Detail label="Title" value={row.contact_title} />
            <Detail label="Email" value={row.contact_email} />
          </div>

          <div className="rounded-md border bg-slate-50 p-4 text-sm">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Outcome
              </h4>
              <span className="text-xs font-semibold tabular-nums">
                Score {o?.outcome_score ?? 0}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <span>Opens: {o?.open_count ?? 0}</span>
              <span>Clicks: {o?.click_count ?? 0}</span>
              <span>
                Replied:{" "}
                {o?.was_replied
                  ? `Yes${o.replied_at ? ` · ${new Date(o.replied_at).toLocaleDateString()}` : ""}`
                  : "No"}
              </span>
              <span>
                Waitlist:{" "}
                {o?.joined_waitlist
                  ? `Joined${o.waitlist_joined_at ? ` · ${new Date(o.waitlist_joined_at).toLocaleDateString()}` : ""}`
                  : "No"}
              </span>
            </div>
            {o?.was_bounced && (
              <div className="mt-3 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
                <div className="font-semibold">
                  Bounced
                  {o.bounced_at
                    ? ` · ${new Date(o.bounced_at).toLocaleDateString()}`
                    : ""}
                </div>
                {o.bounce_reason && (
                  <div className="mt-1 text-rose-700/90">
                    {o.bounce_reason}
                  </div>
                )}
              </div>
            )}
          </div>

          {a ? (
            <div className="rounded-md border bg-slate-50 p-4 text-sm">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                AI-extracted attributes
              </h4>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <Detail label="Tone" value={a.tone} />
                <Detail label="Formality" value={a.formality} />
                <Detail label="Opening" value={a.opening_style} />
                <Detail label="Length" value={`${a.length_category} (${a.word_count} words)`} />
                <Detail label="Personalization" value={a.personalization_level} />
                <Detail label="CTA" value={a.cta_type} />
                <Detail label="Subject style" value={a.subject_line_style} />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                {a.mentions_margins && <Tag>margins</Tag>}
                {a.mentions_roi && <Tag>ROI</Tag>}
                {a.mentions_time_savings && <Tag>time savings</Tag>}
                {a.mentions_growth && <Tag>growth</Tag>}
                {a.mentions_cost_reduction && <Tag>cost reduction</Tag>}
                {a.mentions_social_proof && <Tag>social proof</Tag>}
                {a.mentions_pain_points && <Tag>pain points</Tag>}
              </div>
              {a.value_props_mentioned.length > 0 && (
                <div className="mt-2 text-xs">
                  <span className="text-muted-foreground">Value props: </span>
                  {a.value_props_mentioned.join(", ")}
                </div>
              )}
              {a.pain_points_mentioned.length > 0 && (
                <div className="mt-1 text-xs">
                  <span className="text-muted-foreground">Pain points: </span>
                  {a.pain_points_mentioned.join(", ")}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-dashed bg-slate-50 p-4 text-xs text-muted-foreground">
              No attribute analysis yet. Run "Analyze new emails" to generate one.
            </div>
          )}

          <div className="rounded-md border bg-white p-4 text-sm">
            <button
              type="button"
              onClick={() => setShowHtml((s) => !s)}
              className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              Email body
              {showHtml ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
            {showHtml && row.body_html && (
              <div
                className="mt-3 max-h-96 overflow-y-auto rounded border bg-slate-50 p-3 text-xs"
                dangerouslySetInnerHTML={{ __html: row.body_html }}
              />
            )}
          </div>

          {row.contact_id && (
            <a
              href="/gtm-engine"
              className="block rounded-md border bg-white px-4 py-2 text-center text-xs font-medium hover:bg-slate-50"
            >
              View contact in CRM
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-xs">{value ?? "—"}</div>
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-violet-100 px-2 py-0.5 font-medium text-violet-700">
      {children}
    </span>
  );
}
